/**
 * @fileoverview AR-924 — "Upload for class" modal: header trail, two-step indicator and the active step.
 * Closing with staged-but-not-sent items asks "Discard this upload?" first.
 */

import { useCallback, useState } from 'react'
import { Modal, Stack, Text } from '@mantine/core'
import { modals } from '@mantine/modals'
import { useTranslation } from 'react-i18next'
import type { MatchableStudent } from '../teacherUpload'
import { notifyError, notifySuccess } from '../deliverableDrawer/teacher/feedback'
import { AddFilesStep } from './AddFilesStep'
import { AddLinkForStudentModal } from './AddLinkForStudentModal'
import { MatchStep } from './MatchStep'
import { UploadStepper } from './UploadStepper'
import { coversEveryone } from './stagedItems'
import { useUploadForClass } from './useUploadForClass'

/** Figma draws a 960px dialog on a 1440 frame; real screens asked for more room for file names and selects. */
const MODAL_WIDTH = 'min(1200px, 94vw)'
/** The discard confirmation must sit above the link modal (400). */
const DISCARD_CONFIRM_Z_INDEX = 500
const TITLE_SUBTITLE_GAP = 2

export interface UploadForClassTarget {
  readonly activityId: string
  readonly activityTitle: string
  readonly lessonTitle: string
  readonly projectName: string
  /** `YYYY-MM-DD`; null lets the backend default to the due date. */
  readonly defaultDate: string | null
  readonly students: readonly MatchableStudent[]
  /** Already submitted themselves — not offered, only listed in the step-2 note. */
  readonly excludedNames: readonly string[]
}

interface UploadForClassModalProps {
  readonly target: UploadForClassTarget | null
  readonly onClose: () => void
  readonly onUploaded: () => void
}

type ModalBodyProps = { target: UploadForClassTarget } & Omit<UploadForClassModalProps, 'target'>

function ModalBody({ target, onClose, onUploaded }: ModalBodyProps) {
  const { t } = useTranslation()
  const tr = (key: string, options?: object) => t(`task_tracker.upload_for_class.${key}`, options)
  const state = useUploadForClass(target.activityId, target.students, target.defaultDate)
  const [linkModalOpened, setLinkModalOpened] = useState(false)
  const options = target.students.map(s => ({ value: s.id, label: `${s.firstName} ${s.lastName}`.trim() }))
  const studentNamesOf = (ids: readonly string[]) => {
    if (coversEveryone(ids, options.map(option => option.value))) {
      return tr('whole_class_students', { count: options.length })
    }
    const names = ids.flatMap(id => options.find(option => option.value === id)?.label ?? [])
    return names.length > 0 ? names.join(', ') : null
  }

  const requestClose = useCallback(() => {
    if (state.submitting) return
    if (!state.dirty) {
      onClose()
      return
    }
    modals.openConfirmModal({
      title: tr('leave_title'),
      centered: true,
      zIndex: DISCARD_CONFIRM_Z_INDEX,
      children: <Text size="sm">{tr('leave_body')}</Text>,
      labels: { confirm: tr('leave_confirm'), cancel: tr('leave_cancel') },
      confirmProps: { color: 'red' },
      onConfirm: onClose,
    })
  }, [state.submitting, state.dirty, onClose, t])

  // Partial failure keeps the modal open: a second confirm retries only the failed rows.
  const confirm = async () => {
    const result = await state.confirm()
    onUploaded()
    if (result.failed === 0) {
      notifySuccess(tr('done_toast', { count: result.done }))
      onClose()
    } else {
      notifyError(tr('partial_toast', { done: result.done, total: result.total }))
    }
  }

  return (
    <Modal
      opened
      onClose={requestClose}
      size={MODAL_WIDTH}
      centered
      closeOnClickOutside={!state.submitting}
      closeOnEscape={!state.submitting}
      title={(
        <Stack spacing={TITLE_SUBTITLE_GAP}>
          <Text size="xl" weight={700}>{tr('title')}</Text>
          <Text size="xs" color="dimmed">
            {target.projectName} · {target.lessonTitle} ·{' '}
            <Text span weight={600} color="dark">{target.activityTitle}</Text>
          </Text>
        </Stack>
      )}
    >
      <Stack spacing="md" pt="xs">
        <UploadStepper step={state.step} />
        {state.step === 'add_files' ? (
          <AddFilesStep
            state={state}
            studentNamesOf={studentNamesOf}
            onAddLink={() => setLinkModalOpened(true)}
            onCancel={requestClose}
          />
        ) : (
          <MatchStep
            state={state}
            studentOptions={options}
            excludedNames={target.excludedNames}
            onCancel={requestClose}
            onConfirm={confirm}
          />
        )}
      </Stack>
      <AddLinkForStudentModal
        opened={linkModalOpened}
        studentOptions={options}
        onClose={() => setLinkModalOpened(false)}
        onAdd={state.addLink}
      />
    </Modal>
  )
}

/** Mounted only while a target is set, so every open starts with fresh state. */
export function UploadForClassModal({ target, onClose, onUploaded }: UploadForClassModalProps) {
  if (!target) return null
  return <ModalBody target={target} onClose={onClose} onUploaded={onUploaded} />
}
